// src/pages/Clients.jsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Package } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { getClients, createClient, updateClient, getAFSJobs, getFirm, getClientAFSJobs, downloadAFSJob, downloadAFSJobWord } from '../api/kailux'
import EmptyState from '../components/EmptyState'
import Spinner from '../components/Spinner'
import BackButton from '../components/BackButton'


import { T, alpha } from '../theme/theme'
import Icon, { ICONS } from '../components/Icon'
const MONTHS   = ['January','February','March','April','May','June',
                  'July','August','September','October','November','December']
const TYPES    = ['Pty Ltd','CC','Sole Proprietor','Trust','NPC','Partnership']
// Kept in sync with Signup.jsx / Settings.jsx ENGAGEMENTS lists and with
// backend/main.py's _VALID_ENGAGEMENT_TYPES — 'accounting_officer' is only
// ever shown as an option here when entity_type === 'CC' (see the select
// below), matching the server-side entity_type restriction so the UI never
// offers a combination the backend would silently coerce away.
const ENGAGEMENT_TYPES = [
  { value:'compilation',        label:"Compilation (ISRS 4410)" },
  { value:'review',             label:'Review (ISRE 2400)' },
  { value:'audit',              label:'Audit (ISA)' },
  { value:'accounting_officer', label:"Accounting Officer's Report (CC only)" },
  { value:'in_house',           label:'Prepared in-house (no external accountant)' },
]
const INDUSTRIES = ['Trading','Retail','Construction','Agriculture','Hospitality',
                    'Healthcare','Technology','Logistics','Mining','Professional Services',
                    'Manufacturing','Property','Other']
const BANKS    = ['','FNB','ABSA','Standard Bank','Nedbank','Capitec',
                  'Investec','African Bank','TymeBank','Discovery Bank','Other']

const STEPS = [
  { id:1, label:'Company',       icon:<Icon name="building" /> },
  { id:2, label:'Tax & Finance', icon:<Icon name="receipt" /> },
  { id:3, label:'People',        icon:<Icon name="user" /> },
  { id:4, label:'Addresses',     icon:<Icon name="pin" /> },
]

const ENTITY_SUFFIX = {
  'Pty Ltd':'(Pty) Ltd','CC':'CC','Sole Proprietor':'',
  'Trust':'Trust','NPC':'NPC','Partnership':'',
}
const ENTITY_PERSON = {
  'Pty Ltd':'Director','CC':'Member','Sole Proprietor':'Owner',
  'Trust':'Trustee','NPC':'Director','Partnership':'Partner',
}

// Found 8 Sep 2026 — a new firm's client "Spinesolv (Pty) Ltd" saved as
// "Spinesolv (Pty) Ltd (Pty) Ltd" because this function used to append the
// entity suffix unconditionally. Typing the full registered name (copy-
// pasted from CIPC or a letterhead, suffix and all) is completely normal,
// so this is a routine collision, not a one-off. Guard is anchored on a
// preceding word boundary (not a bare substring check) so short suffixes
// like "CC" don't false-positive on a name that merely ends in those two
// letters — e.g. "Acme Occ" must still get "CC" appended, not be mistaken
// for already carrying it. Case-insensitive and tolerant of a trailing
// full stop/whitespace, so "Acme (Pty) Ltd", "acme (pty) ltd", and
// "Acme (Pty) Ltd." are all recognised as already carrying the suffix.
export const fullCompanyName = (name, entityType) => {
  const suffix = ENTITY_SUFFIX[entityType] || ''
  const base = (name || '').trim()
  if (!base) return ''
  if (!suffix) return base
  const escapedSuffix = suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const alreadyHasSuffix = new RegExp(`(^|\\s)${escapedSuffix}\\.?\\s*$`, 'i').test(base)
  if (alreadyHasSuffix) return base
  return `${base} ${suffix}`
}

// Parse a director_name string (newline-separated) into an array of names
export const parseDirectors = (directorName) => {
  if (!directorName) return []
  return directorName
    .split('\n')
    .map(n => n.trim())
    .filter(Boolean)
}

export const computeStatus = (client, jobs) => {
  if (client.status === 'archived')
    return { label:'Archived', color:T.mt, bg:alpha(T.tx, 0.04), dot:T.mt }
  const cj = jobs.filter(j => j.client_id === client.id)
  if (cj.length === 0)
    return { label:'No AFS yet', color:T.mt, bg:alpha(T.tx, 0.04), dot:T.mt }
  const inProg = cj.some(j => ['mapping','review','upload'].includes(j.status))
  const done   = cj.filter(j => j.status === 'complete')
  if (inProg) return { label:'In Progress', color:T.bl, bg:alpha(T.bl, 0.1), dot:T.bl }
  if (done.length > 0) {
    const latest = [...done].sort((a,b)=>new Date(b.created_at)-new Date(a.created_at))[0]
    const yr = latest?.financial_year ? ` ${latest.financial_year}` : ''
    return { label:`AFS${yr} Done`, color:T.gn, bg:T.gnd, dot:T.gn }
  }
  return { label:'Active', color:T.or, bg:T.ord, dot:T.or }
}

export const computeCompleteness = (client) => {
  const required = [
    client.reg_number, client.director_name, client.nature_of_business,
    client.registered_address, client.bankers, client.tax_reference,
  ]
  if (client.vat_registered === true) required.push(client.vat_number)
  return Math.round(
    (required.filter(f => f && String(f).trim()).length / required.length) * 100
  )
}

export function extractJobFigures(fig = {}) {
  const revenue = fig['Revenue'] || 0
  const cogs    = fig['Cost of Sales'] || 0
  const opex = ['Employee Costs','Occupancy Costs','Communication Expenses',
    'Travel and Motor Expenses','General and Admin Expenses','Professional Fees',
    'Depreciation and Amortisation','Credit Losses','Sales and Marketing']
    .reduce((s, k) => s + (fig[k] || 0), 0)
  const finCosts = fig['Finance Costs'] || 0
  const tax      = fig['Income Tax Expense'] || 0
  const other    = fig['Other Income'] || 0
  const pat = (revenue - cogs) + other - opex - finCosts - tax
  const ppe = Math.max(
    (fig['Property Plant & Equipment'] || 0) - (fig['Accumulated Depreciation'] || 0), 0
  )
  const totalAssets = ppe +
    (fig['Intangible Assets'] || 0) + (fig['Investments'] || 0) + (fig['Deferred Tax Asset'] || 0) +
    (fig['Inventories'] || 0) + (fig['Trade and Other Receivables'] || 0) +
    (fig['Tax Receivables'] || 0) + (fig['Cash and Cash Equivalents'] || 0)
  return { revenue, pat, totalAssets }
}

export function fmtR(n) {
  if (n === 0) return '—'
  const abs = Math.abs(n)
  let s
  if (abs >= 1_000_000) s = `R${(abs / 1_000_000).toFixed(1)}m`
  else if (abs >= 1_000) s = `R${(abs / 1_000).toFixed(0)}k`
  else s = `R${abs.toFixed(0)}`
  return n < 0 ? `(${s})` : s
}

// ── ERROR MESSAGE MAPPER ─────────────────────────────────────────────────────
// Hoisted out of the Clients component (29 Jul 2026) — it never referenced
// component state or closures, so it's safe to test in isolation.
export function friendlyError(e) {
  const detail = e?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (detail?.message) return detail.message
  if (e?.message === 'Network Error' || e?.code === 'ERR_NETWORK')
    return 'Could not reach the server. Check Railway is running and try again.'
  if (e?.response?.status === 401) return 'Session expired — please sign out and sign back in.'
  if (e?.response?.status === 422) return 'Some fields have invalid values — please check and try again.'
  return e?.message || 'An unexpected error occurred. Please try again.'
}

function triggerDownload(blob, filename) {
  const url  = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href  = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// ── Client history panel ──────────────────────────────────────────────────────
function ClientHistoryPanel({ client, allJobs }) {
  const [clientJobs,  setClientJobs]  = useState(null)
  const [loading,     setLoading]     = useState(false)
  const [downloading, setDownloading] = useState(null)
  const [dlError,     setDlError]     = useState(null)
  // Word (.docx) re-download — separate key (`${job.id}:word`) so a Word
  // download in flight/errored never overlaps the PDF button's own state
  // for the same job, mirroring AFSHistory.jsx's dlKind pattern.
  const [dlKind,       setDlKind]     = useState(null)
  const navigate = useNavigate()

  const load = async () => {
    if (clientJobs !== null) return
    setLoading(true)
    try {
      const { data } = await getClientAFSJobs(client.id)
      setClientJobs(data || [])
    } catch {
      setClientJobs([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, []) // eslint-disable-line

  const handleDownload = async (job) => {
    setDownloading(job.id); setDlError(null)
    try {
      const { data } = await downloadAFSJob(job.id)
      const safe = client.name.replace(/ /g,'_').replace(/\//g,'-')
      triggerDownload(data, `${safe}_AFS_${job.financial_year}.pdf`)
    } catch {
      setDlError(job.id)
      setTimeout(() => setDlError(null), 3000)
    } finally {
      setDownloading(null)
    }
  }

  const handleDownloadWord = async (job) => {
    const key = `${job.id}:word`
    setDlKind(key)
    try {
      const { data } = await downloadAFSJobWord(job.id)
      const safe = client.name.replace(/ /g,'_').replace(/\//g,'-')
      triggerDownload(data, `${safe}_AFS_${job.financial_year}.docx`)
    } catch {
      setDlError(key)
      setTimeout(() => setDlError(null), 3000)
    } finally {
      setDlKind(null)
    }
  }

  const ENG = { compilation:'Compilation', review:'Review', audit:'Audit', accounting_officer:"Accounting Officer's Report", in_house:'Prepared in-house' }

  if (loading) return (
    <div style={{ padding:'16px', textAlign:'center', color:T.mt, fontSize:'13px' }}>
      Loading history...
    </div>
  )

  if (!clientJobs || clientJobs.length === 0) return (
    <div style={{ padding:'16px', textAlign:'center' }}>
      <div style={{ fontSize:'13px', color:T.mt, marginBottom:'12px' }}>
        No AFS generated yet on KaiLux
      </div>
      <button onClick={() => navigate('/trial-balance', { state:{ client } })}
        style={{ padding:'8px 18px', borderRadius:'8px', border:'none',
          background:T.prg, color:T.onpri,
          fontFamily:"'Outfit',sans-serif", fontSize:'13px',
          fontWeight:'700', cursor:'pointer' }}>
        Generate First AFS →
      </button>
    </div>
  )

  const completeJobs = clientJobs.filter(j => j.status === 'complete')
  const otherJobs    = clientJobs.filter(j => j.status !== 'complete')

  return (
    <div>
      {completeJobs.length >= 2 && (
        <div style={{ marginBottom:'12px' }}>
          <div style={{ fontSize:'9px', color:T.mt, fontWeight:'700',
            letterSpacing:'1px', marginBottom:'8px' }}>YEAR-ON-YEAR TREND</div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'10px', color:T.lt }}>
              <thead>
                <tr>
                  {['Year','Revenue','Profit','Assets'].map(h => (
                    <th key={h} style={{ textAlign: h==='Year' ? 'left' : 'right',
                      padding:'4px 6px', borderBottom:`1px solid ${T.bd}`,
                      color:T.mt, fontWeight:'700', letterSpacing:'0.5px',
                      fontSize:'9px', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {completeJobs.slice(0, 3).map((job, i) => {
                  const { revenue, pat, totalAssets } = extractJobFigures(job.figures || {})
                  const prev = completeJobs[i + 1]
                  const prevPat = prev ? extractJobFigures(prev.figures || {}).pat : null
                  const profitUp = prevPat !== null ? pat > prevPat : null
                  return (
                    <tr key={job.id} style={{ borderBottom:`1px solid ${T.bd}` }}>
                      <td style={{ padding:'5px 6px', color:T.or, fontWeight:'700', fontSize:'12px' }}>
                        {job.financial_year}
                      </td>
                      <td style={{ padding:'5px 6px', textAlign:'right', color:T.tx, fontWeight:'600' }}>
                        {fmtR(revenue)}
                      </td>
                      <td style={{ padding:'5px 6px', textAlign:'right',
                        color: pat >= 0 ? T.gn : T.rd, fontWeight:'700' }}>
                        {fmtR(pat)}
                        {profitUp !== null && (
                          <span style={{ marginLeft:'4px', fontSize:'8px' }}>
                            {profitUp ? '↑' : '↓'}
                          </span>
                        )}
                      </td>
                      <td style={{ padding:'5px 6px', textAlign:'right', color:T.tx }}>
                        {fmtR(totalAssets)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {otherJobs.length > 0 && (
        <div style={{ marginBottom:'12px', padding:'8px 10px',
          background:alpha(T.bl, 0.06), border:`1px solid ${alpha(T.bl, 0.15)}`,
          borderRadius:'8px', fontSize:'12px', color:T.bl }}>
          <Icon name="hourglass" /> {otherJobs.length} AFS job{otherJobs.length>1?'s':''} currently in progress
        </div>
      )}

      {completeJobs.length > 0 && (
        <div>
          <div style={{ fontSize:'9px', color:T.mt, fontWeight:'700',
            letterSpacing:'1px', marginBottom:'8px' }}>COMPLETED AFS</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
            {completeJobs.map(job => {
              const isDownloading = downloading === job.id
              const hasError      = dlError === job.id
              const date = job.created_at
                ? new Date(job.created_at).toLocaleDateString('en-ZA',
                    { day:'numeric', month:'short', year:'numeric' })
                : '—'
              return (
                <div key={job.id}
                  style={{ display:'flex', alignItems:'center', gap:'8px',
                    padding:'8px 10px', background:T.s2,
                    borderRadius:'8px', border:`1px solid ${T.bd}` }}>
                  <div style={{ width:'8px', height:'8px', borderRadius:'50%',
                    background:T.gn, flexShrink:0 }}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'4px', flexWrap:'wrap' }}>
                      <div style={{ fontSize:'13px', fontWeight:'700', color:T.gn }}>
                        {job.financial_year}
                      </div>
                      {job.signoff_status === 'signed' && (
                        <span style={{ fontSize:'8px', fontWeight:'700', padding:'1px 6px',
                          borderRadius:'6px', background:T.gnd, color:T.gn, letterSpacing:'0.3px' }}>
                          <Icon name="check" /> SIGNED
                        </span>
                      )}
                      {job.signoff_status === 'pending' && (
                        <span style={{ fontSize:'8px', fontWeight:'700', padding:'1px 6px',
                          borderRadius:'6px', background:alpha(T.am, 0.12),
                          color:T.yl, letterSpacing:'0.3px' }}>
                          <Icon name="hourglass" /> AWAITING
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize:'10px', color:T.mt, marginTop:'1px' }}>
                      {ENG[job.engagement_type] || job.engagement_type || 'Compilation'} · {date}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:'4px', flexShrink:0 }}>
                    {job.docx_path && (() => {
                      const wordKey = `${job.id}:word`
                      const isWordDownloading = dlKind === wordKey
                      const hasWordError = dlError === wordKey
                      return (
                        <button
                          onClick={() => handleDownloadWord(job)}
                          disabled={isWordDownloading}
                          title="Re-download Word (.docx)"
                          style={{ padding:'5px 12px', borderRadius:'6px',
                            border:`1px solid ${hasWordError ? T.rd : T.bd}`,
                            background:'transparent',
                            color: hasWordError ? T.rd : T.mt,
                            fontFamily:"'Outfit',sans-serif", fontSize:'10px',
                            fontWeight:'700', cursor: isWordDownloading ? 'not-allowed' : 'pointer',
                            display:'flex', alignItems:'center', gap:'4px',
                            whiteSpace:'nowrap', transition:'all 0.2s' }}>
                          {isWordDownloading ? '...' : hasWordError ? <><Icon name="alert" /> Retry</> : '↓ Word'}
                        </button>
                      )
                    })()}
                    <button
                      onClick={() => handleDownload(job)}
                      disabled={isDownloading}
                      title="Re-download PDF"
                      style={{ padding:'5px 12px', borderRadius:'6px', border:'none',
                        background: hasError ? alpha(T.rd, 0.15) : T.prg,
                        opacity: isDownloading ? 0.6 : 1,
                        color: hasError ? T.rd : T.onpri,
                        fontFamily:"'Outfit',sans-serif", fontSize:'10px',
                        fontWeight:'700', cursor: isDownloading ? 'not-allowed' : 'pointer',
                        display:'flex', alignItems:'center', gap:'4px',
                        whiteSpace:'nowrap', transition:'all 0.2s' }}>
                      {isDownloading ? (
                        <>
                          <span style={{ width:'8px', height:'8px',
                            border:`1.5px solid ${alpha(T.onpri, 0.3)}`,
                            borderTopColor:T.onpri, borderRadius:'50%',
                            animation:'spin 0.8s linear infinite', display:'inline-block' }}/>
                          ...
                        </>
                      ) : hasError ? <><Icon name="alert" /> Retry</> : '↓ PDF'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN CLIENTS PAGE
// ═════════════════════════════════════════════════════════════════════════════
export default function Clients() {
  const [clients,        setClients]        = useState([])
  const [jobs,           setJobs]           = useState([])
  const [loading,        setLoading]        = useState(true)
  const [loadError,      setLoadError]      = useState('')
  const [modalMode,      setModalMode]      = useState(null)
  const [editTarget,     setEditTarget]     = useState(null)
  const [step,           setStep]           = useState(1)
  const [saving,         setSaving]         = useState(false)
  const [saveError,      setSaveError]      = useState('')
  const [search,         setSearch]         = useState('')
  const [showArchived,   setShowArchived]   = useState(false)
  const [archiveConfirm, setArchiveConfirm] = useState(null)
  const [firmDefaults,   setFirmDefaults]   = useState({ year_end_month:'February', engagement_type:'compilation' })
  const [expandedHistory, setExpandedHistory] = useState({})

  const emptyForm = () => ({
    name:'', trading_name:'', reg_number:'',
    entity_type:'Pty Ltd', industry:'Trading',
    engagement_type: firmDefaults.engagement_type || 'compilation',
    date_of_incorporation:'',
    year_end_month: firmDefaults.year_end_month || 'February',
    vat_registered: false, vat_number:'',
    tax_reference:'', bankers:'', nature_of_business:'',
    director_name:'',       // all directors, newline-separated
    signing_director:'',    // the one director who signs the AFS
    registered_address:'', postal_address:'', same_address:true,
  })

  const [form, setForm] = useState(emptyForm())
  const { firm } = useAuth()
  const navigate = useNavigate()
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    if (firm?.id) {
      getFirm(firm.id)
        .then(({ data }) => {
          setFirmDefaults({
            year_end_month:  data.year_end_month  || 'February',
            engagement_type: data.engagement_type || 'compilation',
          })
        })
        .catch(() => {})
      load()
    }
  }, [firm])

  const load = async () => {
    setLoadError('')
    try {
      const [{ data: c }, { data: j }] = await Promise.all([
        getClients(firm.id),
        getAFSJobs(firm.id),
      ])
      setClients(c || [])
      setJobs(j || [])
    } catch(e) {
      console.error('Failed to load clients:', e)
      // Show a meaningful error so users know what's wrong
      const status = e?.response?.status
      const detail = e?.response?.data?.detail || e?.message || ''
      if (status === 401) {
        setLoadError('Session expired — please sign out and sign back in.')
      } else if (!navigator.onLine || e?.code === 'ERR_NETWORK') {
        setLoadError('Cannot reach the server. Check that Railway is running and your network is connected.')
      } else {
        setLoadError(`Could not load clients (${status || 'network error'}${detail ? ': ' + detail : ''}). Please refresh the page.`)
      }
    } finally {
      setLoading(false)
    }
  }

  const toggleHistory = (clientId) => {
    setExpandedHistory(prev => ({ ...prev, [clientId]: !prev[clientId] }))
  }

  const openAdd = () => {
    setForm(emptyForm()); setStep(1); setSaveError(''); setModalMode('add')
  }

  const openEdit = (client) => {
    setForm({
      name:                  client.name || '',
      trading_name:          client.trading_name || '',
      reg_number:            client.reg_number || '',
      entity_type:           client.entity_type || 'Pty Ltd',
      industry:              client.industry || 'Trading',
      // Falls back to the firm default (not hardcoded 'compilation') so an
      // existing client that's never had an explicit override yet shows
      // what will actually be used, matching the backend's own cascade.
      engagement_type:       client.engagement_type || firmDefaults.engagement_type || 'compilation',
      date_of_incorporation: client.date_of_incorporation || '',
      year_end_month:        client.year_end_month || 'February',
      vat_registered:        client.vat_registered === true,
      vat_number:            client.vat_number || '',
      tax_reference:         client.tax_reference || '',
      bankers:               client.bankers || '',
      nature_of_business:    client.nature_of_business || '',
      director_name:         client.director_name || '',
      signing_director:      client.signing_director || client.director_name?.split('\n')[0]?.trim() || '',
      registered_address:    client.registered_address || '',
      postal_address:        client.postal_address || '',
      same_address: !client.postal_address || client.postal_address === client.registered_address,
    })
    setEditTarget(client); setStep(1); setSaveError(''); setModalMode('edit')
  }

  const closeModal = () => { setModalMode(null); setEditTarget(null); setSaveError(''); setStep(1) }

  const validateStep = () => {
    if (step === 1 && !form.name.trim()) { setSaveError('Company name is required.'); return false }
    if (step === 3 && !form.director_name.trim()) {
      setSaveError(`At least one ${ENTITY_PERSON[form.entity_type]} full name is required.`); return false
    }
    if (step === 4 && !form.registered_address.trim()) { setSaveError('Registered address is required.'); return false }
    setSaveError(''); return true
  }

  const nextStep = () => { if (validateStep()) setStep(s => s + 1) }
  const prevStep = () => { setSaveError(''); setStep(s => s - 1) }

  const buildPayload = () => {
    // Derive signing_director: if only one director, use their name;
    // if multiple and user picked one, use that; else use first name.
    const directors = parseDirectors(form.director_name)
    const signer = form.signing_director?.trim()
      || (directors.length > 0 ? directors[0] : '')

    return {
      firm_id:               firm.id,
      name: modalMode === 'add'
        ? fullCompanyName(form.name, form.entity_type)
        : form.name.trim(),
      trading_name:          form.trading_name.trim(),
      reg_number:            form.reg_number.trim(),
      entity_type:           form.entity_type,
      industry:              form.industry,
      // Client-level override (Aug 2026) — previously collected nowhere and
      // never sent, so every client silently used the firm-wide default
      // regardless of entity type. A CC selecting 'accounting_officer' is
      // enforced server-side (_resolve_engagement_type); anything else here
      // gets coerced back to 'compilation' if it doesn't apply.
      engagement_type:       form.entity_type === 'CC' ? form.engagement_type : (form.engagement_type === 'accounting_officer' ? 'compilation' : form.engagement_type),
      date_of_incorporation: form.date_of_incorporation,
      year_end_month:        form.year_end_month,
      vat_registered:        form.vat_registered,
      vat_number:            form.vat_registered ? form.vat_number.trim() : '',
      tax_reference:         form.tax_reference.trim(),
      bankers:               form.bankers,
      nature_of_business:    form.nature_of_business.trim(),
      director_name:         form.director_name.trim(),
      signing_director:      signer,
      registered_address:    form.registered_address.trim(),
      postal_address:        form.same_address ? form.registered_address.trim() : form.postal_address.trim(),
    }
  }

  const handleCreate = async () => {
    if (!validateStep()) return
    setSaving(true); setSaveError('')
    try {
      await createClient(buildPayload())
      closeModal(); load()
    } catch(e) {
      // Duplicate registration number for this firm (Phase 6, Issue B) —
      // soft warning: let the user explicitly confirm they want to proceed
      // anyway (e.g. a genuine branch, or a re-entry after a typo elsewhere),
      // rather than silently blocking client creation.
      const detail = e?.response?.status === 409 ? e.response.data?.detail : null
      if (detail?.duplicate) {
        const proceed = window.confirm(detail.message)
        if (proceed) {
          try {
            await createClient({ ...buildPayload(), confirm_duplicate: true })
            closeModal(); load()
          } catch (e2) {
            setSaveError(friendlyError(e2))
          }
        }
      } else {
        setSaveError(friendlyError(e))
      }
    } finally { setSaving(false) }
  }

  const handleUpdate = async () => {
    if (!validateStep()) return
    setSaving(true); setSaveError('')
    try {
      const p = buildPayload(); delete p.firm_id
      await updateClient(editTarget.id, p); closeModal(); load()
    }
    catch(e) { setSaveError(friendlyError(e)) }
    finally { setSaving(false) }
  }

  const handleArchive   = async (client) => { try { await updateClient(client.id, { status:'archived' }); setArchiveConfirm(null); load() } catch(e) { console.error(e) } }
  const handleUnarchive = async (client) => { try { await updateClient(client.id, { status:'active' }); load() } catch(e) { console.error(e) } }

  // ── Form helpers ──────────────────────────────────────────────────────────
  const inp = (label, key, ph='', hint='', optional=false) => (
    <div style={{ marginBottom:'14px' }}>
      <label style={{ display:'block', fontSize:'10px', fontWeight:'700', color:T.lt, letterSpacing:'1px', marginBottom:'5px' }}>
        {label.toUpperCase()}
        {optional && <span style={{ color:T.mt, fontWeight:'400', marginLeft:'6px', fontSize:'9px' }}>(optional)</span>}
      </label>
      <input value={form[key]} onChange={e => set(key, e.target.value)} placeholder={ph}
        style={{ width:'100%', padding:'10px 12px', background:T.s3, border:`1px solid ${T.bd}`,
          borderRadius:'8px', color:T.tx, fontSize:'14px', fontFamily:"'Outfit',sans-serif",
          outline:'none', boxSizing:'border-box' }}
        onFocus={e => e.target.style.borderColor = T.or}
        onBlur={e  => e.target.style.borderColor = T.bd}/>
      {hint && <div style={{ fontSize:'10px', color:T.mt, marginTop:'4px', lineHeight:'1.5' }}>{hint}</div>}
    </div>
  )

  const ta = (label, key, ph='', hint='', optional=false, rows=3) => (
    <div style={{ marginBottom:'14px' }}>
      <label style={{ display:'block', fontSize:'10px', fontWeight:'700', color:T.lt, letterSpacing:'1px', marginBottom:'5px' }}>
        {label.toUpperCase()}
        {optional && <span style={{ color:T.mt, fontWeight:'400', marginLeft:'6px', fontSize:'9px' }}>(optional)</span>}
      </label>
      <textarea value={form[key]} onChange={e => set(key, e.target.value)} placeholder={ph} rows={rows}
        style={{ width:'100%', padding:'10px 12px', background:T.s3, border:`1px solid ${T.bd}`,
          borderRadius:'8px', color:T.tx, fontSize:'14px', fontFamily:"'Outfit',sans-serif",
          outline:'none', boxSizing:'border-box', resize:'vertical', lineHeight:'1.6' }}
        onFocus={e => e.target.style.borderColor = T.or}
        onBlur={e  => e.target.style.borderColor = T.bd}/>
      {hint && <div style={{ fontSize:'10px', color:T.mt, marginTop:'4px', lineHeight:'1.5' }}>{hint}</div>}
    </div>
  )

  const sel = (label, key, options, optional=false) => (
    <div style={{ marginBottom:'14px' }}>
      <label style={{ display:'block', fontSize:'10px', fontWeight:'700', color:T.lt, letterSpacing:'1px', marginBottom:'5px' }}>
        {label.toUpperCase()}
        {optional && <span style={{ color:T.mt, fontWeight:'400', marginLeft:'6px', fontSize:'9px' }}>(optional)</span>}
      </label>
      <select value={form[key]} onChange={e => set(key, e.target.value)}
        style={{ width:'100%', padding:'10px 12px', background:T.s3, border:`1px solid ${T.bd}`,
          borderRadius:'8px', color:T.tx, fontSize:'14px', fontFamily:"'Outfit',sans-serif",
          outline:'none', boxSizing:'border-box' }}>
        {options.map(o => <option key={o} value={o} style={{ background:T.s2 }}>{o}</option>)}
      </select>
    </div>
  )

  const stepContent = () => {
    const isEdit  = modalMode === 'edit'
    const person  = ENTITY_PERSON[form.entity_type] || 'Director'
    const preview = fullCompanyName(form.name, form.entity_type)
    // Parse directors to determine if we need a signing-director picker
    const directors = parseDirectors(form.director_name)
    const hasMultipleDirectors = directors.length > 1

    if (step === 1) return (<>
      <div style={{ fontSize:'12px', color:T.or, fontWeight:'700', letterSpacing:'2px', marginBottom:'16px' }}>COMPANY DETAILS</div>
      <div style={{ marginBottom:'14px' }}>
        <label style={{ display:'block', fontSize:'10px', fontWeight:'700', color:T.lt, letterSpacing:'1px', marginBottom:'5px' }}>REGISTERED COMPANY NAME</label>
        <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Africakai"
          style={{ width:'100%', padding:'10px 12px', background:T.s3, border:`1px solid ${T.bd}`,
            borderRadius:'8px', color:T.tx, fontSize:'14px', fontFamily:"'Outfit',sans-serif",
            outline:'none', boxSizing:'border-box' }}
          onFocus={e => e.target.style.borderColor = T.or}
          onBlur={e  => e.target.style.borderColor = T.bd}/>
        {!isEdit && form.name.trim() && (
          <div style={{ marginTop:'6px', padding:'8px 12px', background:alpha(T.or, 0.06),
            border:`1px solid ${alpha(T.or, 0.2)}`, borderRadius:'6px', fontSize:'12px', color:T.or }}>
            Will appear on AFS as: <strong>{preview}</strong>
          </div>
        )}
        <div style={{ fontSize:'10px', color:T.mt, marginTop:'4px' }}>
          {isEdit ? 'Full registered name including entity suffix' : 'Enter name only — the entity suffix is added automatically'}
        </div>
      </div>
      {inp('Trading Name', 'trading_name', 'e.g. Africakai Distributors', 'Only if the business trades under a different name', true)}
      {inp('CIPC Registration Number', 'reg_number', 'e.g. 2021/445821/07', '', true)}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
        {sel('Entity Type', 'entity_type', TYPES)}
        {sel('Industry', 'industry', INDUSTRIES)}
      </div>
      <div style={{ marginBottom:'14px' }}>
        <label style={{ display:'block', fontSize:'10px', fontWeight:'700', color:T.lt, letterSpacing:'1px', marginBottom:'5px' }}>
          ENGAGEMENT TYPE
          <span style={{ color:T.mt, fontWeight:'400', marginLeft:'6px', fontSize:'9px' }}>
            (overrides the firm default of {ENGAGEMENT_TYPES.find(e => e.value === firmDefaults.engagement_type)?.label || 'Compilation'} for this client)
          </span>
        </label>
        <select value={form.engagement_type} onChange={e => set('engagement_type', e.target.value)}
          style={{ width:'100%', padding:'10px 12px', background:T.s3, border:`1px solid ${T.bd}`,
            borderRadius:'8px', color:T.tx, fontSize:'14px', fontFamily:"'Outfit',sans-serif",
            outline:'none', boxSizing:'border-box' }}>
          {ENGAGEMENT_TYPES
            .filter(e => e.value !== 'accounting_officer' || form.entity_type === 'CC')
            .filter(e => e.value !== 'in_house' || form.entity_type !== 'CC')
            .map(e => <option key={e.value} value={e.value} style={{ background:T.s2 }}>{e.label}</option>)}
        </select>
        {form.entity_type === 'CC' && (
          <div style={{ fontSize:'10px', color:T.mt, marginTop:'4px' }}>
            Close Corporations are usually reported on by an Accounting Officer under the CC Act, rather than a compilation/review/audit engagement.
          </div>
        )}
        {form.engagement_type === 'in_house' && (
          <div style={{ fontSize:'10px', color:T.mt, marginTop:'4px' }}>
            In-house statements are internally compiled. Under the Companies Act and Regulations they may go out without an audit or independent review only if the company is owner-managed (every shareholder is also a director) and its Public Interest Score is below 100. Otherwise an audit (score of 100 or more) or an independent review (non-owner-managed, score below 100) is also required. Enter the preparer's name and professional designation in Settings so it appears on the first page.
          </div>
        )}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
        {sel('Financial Year End Month', 'year_end_month', MONTHS)}
        <div style={{ marginBottom:'14px' }}>
          <label style={{ display:'block', fontSize:'10px', fontWeight:'700', color:T.lt, letterSpacing:'1px', marginBottom:'5px' }}>
            DATE OF INCORPORATION <span style={{ color:T.mt, fontWeight:'400', fontSize:'9px' }}>(optional)</span>
          </label>
          <input type="date" value={form.date_of_incorporation}
            onChange={e => set('date_of_incorporation', e.target.value)}
            style={{ width:'100%', padding:'10px 12px', background:T.s3, border:`1px solid ${T.bd}`,
              borderRadius:'8px', color:T.tx, fontSize:'14px', fontFamily:"'Outfit',sans-serif",
              outline:'none', boxSizing:'border-box', colorScheme:'dark' }}
            onFocus={e => e.target.style.borderColor = T.or}
            onBlur={e  => e.target.style.borderColor = T.bd}/>
        </div>
      </div>
    </>)

    if (step === 2) return (<>
      <div style={{ fontSize:'12px', color:T.or, fontWeight:'700', letterSpacing:'2px', marginBottom:'16px' }}>TAX & FINANCIAL DETAILS</div>
      <div style={{ marginBottom:'16px' }}>
        <div style={{ fontSize:'10px', fontWeight:'700', color:T.lt, letterSpacing:'1px', marginBottom:'10px' }}>IS THIS COMPANY REGISTERED FOR VAT?</div>
        <div style={{ display:'flex', gap:'8px' }}>
          {[true, false].map(val => (
            <label key={String(val)} style={{ flex:1, display:'flex', gap:'8px', alignItems:'center',
              padding:'10px 14px', borderRadius:'8px', cursor:'pointer',
              background: form.vat_registered === val ? T.ord : T.s2,
              border:`1.5px solid ${form.vat_registered === val ? T.or : T.bd}`, transition:'all 0.2s' }}>
              <input type="radio" name="vat_reg" checked={form.vat_registered === val}
                onChange={() => set('vat_registered', val)} style={{ accentColor:T.or }}/>
              <span style={{ fontSize:'14px', fontWeight:'600', color: form.vat_registered === val ? T.or : T.tx }}>
                {val ? 'Yes — VAT registered' : 'No — not registered'}
              </span>
            </label>
          ))}
        </div>
      </div>
      {form.vat_registered && (
        <div style={{ marginBottom:'14px', padding:'12px', background:alpha(T.or, 0.04),
          border:`1px solid ${alpha(T.or, 0.15)}`, borderRadius:'8px' }}>
          {inp('VAT Registration Number', 'vat_number', 'e.g. 4560123456', 'As issued by SARS')}
        </div>
      )}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
        {inp('Income Tax Reference Number', 'tax_reference', 'e.g. 9123456789', 'SARS income tax reference', true)}
        {sel('Primary Banker', 'bankers', BANKS, true)}
      </div>
      {ta('Nature of Business & Principal Activities', 'nature_of_business',
        'e.g. Wholesale distribution of building materials to contractors in Limpopo province.',
        "Appears word-for-word in the Directors' Report and General Information page of the AFS.", false, 4)}
    </>)

    if (step === 3) return (<>
      <div style={{ fontSize:'12px', color:T.or, fontWeight:'700', letterSpacing:'2px', marginBottom:'16px' }}>
        {person.toUpperCase()}S IN OFFICE
      </div>
      <div style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'8px',
        padding:'12px 14px', marginBottom:'16px', fontSize:'13px', color:T.mt, lineHeight:'1.7' }}>
        <Icon name="info" /> List all {person.toLowerCase()}s who held office during the financial year.
        One name per line.
      </div>

      {ta(`${person} Full Name(s)`, 'director_name',
        `e.g. Thavha Michael Madembe${form.entity_type !== 'Sole Proprietor' ? '\nSarah Jane Ndlovu' : ''}`,
        `Full first name(s) and surname for each ${person.toLowerCase()}. New line per person.`, false, 4)}

      {/* ── SIGNING DIRECTOR PICKER ─────────────────────────────────────── */}
      {/* Always show this so user can pick (or confirm) who signs the AFS */}
      {directors.length > 0 && (
        <div style={{ marginTop:'4px' }}>
          <div style={{ fontSize:'10px', fontWeight:'700', color:T.lt, letterSpacing:'1px', marginBottom:'8px' }}>
            WHO WILL SIGN THE AFS?
          </div>
          <div style={{ fontSize:'12px', color:T.mt, marginBottom:'10px', lineHeight:'1.6' }}>
            Only one {person.toLowerCase()}'s signature line appears on the financial statements.
            Select the signing {person.toLowerCase()} below.
          </div>

          {directors.length === 1 ? (
            // Single director — auto-select, just show confirmation
            <div style={{ padding:'10px 14px', background:T.gnd,
              border:`1px solid ${alpha(T.gn, 0.25)}`, borderRadius:'8px',
              fontSize:'13px', color:T.gn, fontWeight:'600' }}>
              <Icon name="check" /> {directors[0]} will sign the AFS
            </div>
          ) : (
            // Multiple directors — show radio buttons
            <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
              {directors.map((name, i) => (
                <label key={i}
                  style={{ display:'flex', gap:'12px', alignItems:'center',
                    padding:'10px 14px', borderRadius:'8px', cursor:'pointer',
                    background: form.signing_director === name ? T.ord : T.s2,
                    border:`1.5px solid ${form.signing_director === name ? T.or : T.bd}`,
                    transition:'all 0.15s' }}>
                  <input
                    type="radio"
                    name="signing_director"
                    value={name}
                    checked={form.signing_director === name}
                    onChange={() => set('signing_director', name)}
                    style={{ accentColor:T.or, flexShrink:0 }}
                  />
                  <span style={{ fontSize:'14px', fontWeight:'600',
                    color: form.signing_director === name ? T.or : T.tx }}>
                    {name}
                  </span>
                </label>
              ))}
              {/* Option to leave blank if undecided */}
              <label style={{ display:'flex', gap:'12px', alignItems:'center',
                padding:'10px 14px', borderRadius:'8px', cursor:'pointer',
                background: !form.signing_director ? T.ord : T.s2,
                border:`1.5px solid ${!form.signing_director ? T.or : T.bd}`,
                transition:'all 0.15s' }}>
                <input
                  type="radio"
                  name="signing_director"
                  value=""
                  checked={!form.signing_director}
                  onChange={() => set('signing_director', '')}
                  style={{ accentColor:T.or, flexShrink:0 }}
                />
                <span style={{ fontSize:'14px', fontWeight:'600',
                  color: !form.signing_director ? T.or : T.mt }}>
                  Decide later
                </span>
              </label>
            </div>
          )}
        </div>
      )}

      {/* Show picker also when user is still typing directors */}
      {directors.length === 0 && form.director_name.trim() === '' && (
        <div style={{ padding:'10px 14px', background:T.s2,
          border:`1px solid ${T.bd}`, borderRadius:'8px',
          fontSize:'12px', color:T.mt }}>
          Enter the {person.toLowerCase()}(s) above — you will then select who signs the AFS.
        </div>
      )}
    </>)

    if (step === 4) return (<>
      <div style={{ fontSize:'12px', color:T.or, fontWeight:'700', letterSpacing:'2px', marginBottom:'16px' }}>ADDRESSES</div>
      {ta('Registered / Business Address', 'registered_address',
        'e.g. 25 Rissik Street\nPolokwane\nLimpopo\n0699',
        'Physical street address as registered with CIPC', false, 4)}
      <label style={{ display:'flex', gap:'8px', alignItems:'center',
        fontSize:'13px', color:T.mt, cursor:'pointer', marginBottom:'14px' }}>
        <input type="checkbox" checked={form.same_address}
          onChange={e => set('same_address', e.target.checked)}
          style={{ accentColor:T.or, width:'14px', height:'14px' }}/>
        Postal address is the same as registered address
      </label>
      {!form.same_address && ta('Postal Address', 'postal_address', 'e.g. P.O. Box 1234\nPolokwane\n0700', '', true, 4)}
    </>)
  }

  const activeClients   = clients.filter(c => c.status !== 'archived')
  const archivedClients = clients.filter(c => c.status === 'archived')
  const displayClients  = showArchived ? archivedClients : activeClients
  const filtered = displayClients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.industry?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif", color:T.tx }}>

      {/* ── ARCHIVE CONFIRM ── */}
      {archiveConfirm && (
        <div onClick={() => setArchiveConfirm(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, padding:'24px' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'16px',
              padding:'24px', maxWidth:'400px', width:'100%',
              animation:'kailuxModalIn 0.25s ease' }}>
            <div style={{ marginBottom:'12px' }}><Package size={26} strokeWidth={1.5} color={T.mt}/></div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'18px', fontWeight:'800', marginBottom:'8px' }}>Archive this client?</div>
            <div style={{ fontSize:'14px', color:T.mt, lineHeight:'1.7', marginBottom:'20px' }}>
              <strong style={{ color:T.tx }}>{archiveConfirm.name}</strong> will be hidden from your active list.
              All history is preserved and can be restored at any time.
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              <button onClick={() => handleArchive(archiveConfirm)}
                style={{ flex:1, padding:'12px', borderRadius:'8px', border:'none',
                  background:alpha(T.rd, 0.15), color:T.rd,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
                Archive Client
              </button>
              <button onClick={() => setArchiveConfirm(null)}
                style={{ flex:1, padding:'12px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                  background:'transparent', color:T.mt,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px', cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD/EDIT MODAL ── */}
      {modalMode && (
        <div onClick={closeModal}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:'24px' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'20px',
              width:'100%', maxWidth:'540px', maxHeight:'90vh', display:'flex', flexDirection:'column',
              boxShadow:T.shl,
              animation:'kailuxModalIn 0.25s ease' }}>
            <div style={{ padding:'24px 28px 0', flexShrink:0 }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'20px', fontWeight:'800', marginBottom:'4px' }}>
                {modalMode === 'add' ? 'Add New Client' : `Edit — ${editTarget?.name}`}
              </div>
              <div style={{ fontSize:'13px', color:T.mt, marginBottom:'20px' }}>
                {modalMode === 'add' ? 'Complete all sections for a full AFS General Information page' : 'Update client details — changes apply to the next AFS generated'}
              </div>
              <div style={{ display:'flex', marginBottom:'24px' }}>
                {STEPS.map((s, i) => (
                  <div key={s.id} style={{ display:'flex', alignItems:'center', flex:1 }}>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flex:1 }}>
                      <div style={{ width:'30px', height:'30px', borderRadius:'50%',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        fontSize:'13px', fontWeight:'800', transition:'all 0.3s',
                        background: step > s.id ? T.gn : step === s.id ? T.or : T.s2,
                        color: step >= s.id ? T.onpri : T.mt,
                        border: step < s.id ? `1px solid ${T.bd}` : 'none' }}>
                        {step > s.id ? <Icon name="check" /> : s.id}
                      </div>
                      <div style={{ fontSize:'8px', fontWeight:'600', marginTop:'4px',
                        color: step === s.id ? T.or : T.mt, letterSpacing:'0.5px' }}>
                        {s.label.toUpperCase()}
                      </div>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div style={{ height:'1px', flex:1, marginBottom:'18px',
                        background: step > s.id ? T.gn : T.bd, transition:'background 0.3s' }}/>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding:'0 28px', overflowY:'auto', flex:1 }}>
              {saveError && (
                <div style={{ background:T.rdd, border:`1px solid ${alpha(T.rd, 0.3)}`,
                  borderRadius:'8px', padding:'10px 14px', marginBottom:'14px',
                  fontSize:'13px', color:T.rd, lineHeight:'1.5' }}>
                  <Icon name="alert" /> {saveError}
                </div>
              )}
              {stepContent()}
            </div>
            <div style={{ padding:'16px 28px 24px', flexShrink:0, borderTop:`1px solid ${T.bd}`,
              marginTop:'8px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <button onClick={step === 1 ? closeModal : prevStep}
                style={{ padding:'10px 20px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                  background:'transparent', color:T.mt, fontFamily:"'Outfit',sans-serif",
                  fontSize:'14px', cursor:'pointer' }}>
                {step === 1 ? 'Cancel' : '← Back'}
              </button>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <span style={{ fontSize:'12px', color:T.mt }}>Step {step} of {STEPS.length}</span>
                {step < STEPS.length ? (
                  <button onClick={nextStep}
                    style={{ padding:'10px 24px', borderRadius:'8px', border:'none',
                      background:T.prg, color:T.onpri,
                      fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
                    Continue →
                  </button>
                ) : (
                  <button onClick={modalMode === 'add' ? handleCreate : handleUpdate} disabled={saving}
                    style={{ padding:'10px 24px', borderRadius:'8px', border:'none',
                      background: T.prg, opacity: saving ? 0.6 : 1,
                      color:T.onpri, fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      display:'flex', alignItems:'center', gap:'8px' }}>
                    {saving ? (
                      <><span style={{ width:'12px', height:'12px', border:`2px solid ${alpha(T.onpri, 0.3)}`,
                        borderTopColor:T.onpri, borderRadius:'50%', animation:'spin 0.8s linear infinite',
                        display:'inline-block' }}/>Saving...</>
                    ) : modalMode === 'add' ? 'Save Client →' : 'Save Changes →'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── NAV ── */}
      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`, padding:'0 32px',
        height:'62px', display:'flex', alignItems:'center', justifyContent:'space-between',
        position:'sticky', top:0, zIndex:40 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'16px' }}>
          <BackButton onClick={() => navigate('/dashboard')} label="Dashboard"
            textColor={T.mt} borderColor={T.bd} accentColor={T.or} />
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'18px', fontWeight:'800' }}>
            Kai<span style={{color:T.or}}>Lux</span>
          </div>
          <span style={{ fontSize:'14px', color:T.mt }}>/ Clients</span>
        </div>
        <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
          <button onClick={() => navigate('/afs-history')}
            style={{ padding:'9px 16px', borderRadius:'8px',
              border:`1px solid ${T.bd}`, background:'transparent',
              color:T.mt, fontFamily:"'Outfit',sans-serif",
              fontSize:'14px', fontWeight:'600', cursor:'pointer', transition:'all 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor=T.or; e.currentTarget.style.color=T.or }}
            onMouseLeave={e => { e.currentTarget.style.borderColor=T.bd; e.currentTarget.style.color=T.mt }}>
            <Icon name="log" /> AFS History
          </button>
          <button onClick={openAdd}
            style={{ padding:'9px 20px', borderRadius:'8px', border:'none',
              background:T.prg, color:T.onpri,
              fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
            + Add Client
          </button>
        </div>
      </div>

      {/* ── CLIENT LIST ── */}
      <div style={{ padding:'32px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
          marginBottom:'24px', flexWrap:'wrap', gap:'12px' }}>
          <div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'24px', fontWeight:'800', marginBottom:'4px' }}>Client Portfolio</div>
            <div style={{ fontSize:'14px', fontWeight:'400', color:T.mt }}>{activeClients.length} active · {archivedClients.length} archived</div>
          </div>
          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            <button onClick={() => setShowArchived(v => !v)}
              style={{ padding:'8px 14px', borderRadius:'8px',
                border:`1px solid ${showArchived ? T.or : T.bd}`,
                background: showArchived ? T.ord : 'transparent',
                color: showArchived ? T.or : T.mt,
                fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'600',
                cursor:'pointer', transition:'all 0.2s' }}>
              {showArchived ? '← Active Clients' : <><Icon name="archive" /> Archived</>}
            </button>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..."
              style={{ padding:'10px 16px', background:T.s1, border:`1px solid ${T.bd}`,
                borderRadius:'8px', color:T.tx, fontSize:'14px',
                fontFamily:"'Outfit',sans-serif", outline:'none', width:'200px' }}/>
          </div>
        </div>

        {/* Load error banner */}
        {loadError && (
          <div style={{ background:T.rdd, border:`1px solid ${alpha(T.rd, 0.3)}`,
            borderRadius:'8px', padding:'14px 18px', marginBottom:'20px',
            fontSize:'14px', color:T.rd, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span><Icon name="alert" /> {loadError}</span>
            <button onClick={load}
              style={{ padding:'6px 14px', borderRadius:'6px', border:`1px solid ${T.rd}`,
                background:'transparent', color:T.rd, fontFamily:"'Outfit',sans-serif",
                fontSize:'13px', fontWeight:'600', cursor:'pointer', flexShrink:0, marginLeft:'12px' }}>
              Retry
            </button>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign:'center', padding:'60px' }}><Spinner label='Loading clients…'/></div>
        ) : filtered.length === 0 && !loadError ? (
          <EmptyState
            size="large"
            icon={showArchived ? ICONS.archive : ICONS.users}
            title={search ? 'No clients found' : showArchived ? 'No archived clients' : 'No clients yet'}
            subtitle={search ? 'Try a different search term' : showArchived ? 'Archived clients will appear here' : 'Add your first client to get started'}
            action={!search && !showArchived && (
              <button onClick={openAdd}
                style={{ padding:'12px 28px', borderRadius:'8px', border:'none',
                  background:T.prg, color:T.onpri,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
                + Add First Client
              </button>
            )}
          />
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:'12px' }}>
            {filtered.map(client => {
              const status       = computeStatus(client, jobs)
              const completeness = computeCompleteness(client)
              const isArchived   = client.status === 'archived'
              const histOpen     = !!expandedHistory[client.id]
              // Show signing director badge if set and different from first listed director
              const directors    = parseDirectors(client.director_name)
              const signer       = client.signing_director || directors[0] || ''

              return (
                <div key={client.id}
                  style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px',
                    padding:'20px', opacity: isArchived ? 0.7 : 1, transition:'all 0.2s' }}
                  onMouseEnter={e => { if(!isArchived) { e.currentTarget.style.borderColor = alpha(T.or, 0.38); e.currentTarget.style.transform='translateY(-2px)' } }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.bd; e.currentTarget.style.transform='translateY(0)' }}>

                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'12px' }}>
                    <div style={{ width:'40px', height:'40px', borderRadius:'8px',
                      background: isArchived ? T.s2 : T.ord,
                      border:`1px solid ${isArchived ? T.bd : alpha(T.or, 0.19)}`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:'16px', fontWeight:'800', color: isArchived ? T.mt : T.or }}>
                      {client.name[0]}
                    </div>
                    <span style={{ fontSize:'9px', fontWeight:'700', padding:'3px 9px',
                      borderRadius:'6px', background:status.bg, color:status.color,
                      display:'flex', alignItems:'center', gap:'5px' }}>
                      <span style={{ width:'5px', height:'5px', borderRadius:'50%',
                        background:status.dot, display:'inline-block', flexShrink:0 }}/>
                      {status.label}
                    </span>
                  </div>

                  <div style={{ fontSize:'15px', fontWeight:'700', marginBottom:'2px', lineHeight:'1.3' }}>{client.name}</div>
                  {client.trading_name && <div style={{ fontSize:'12px', color:T.or, marginBottom:'4px' }}>t/a {client.trading_name}</div>}
                  <div style={{ fontSize:'12px', color:T.mt, marginBottom:'10px' }}>
                    {client.entity_type} · {client.industry} · Year end: {client.year_end_month}
                  </div>

                  <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', marginBottom:'12px' }}>
                    {client.reg_number && (
                      <span style={{ fontSize:'9px', padding:'2px 7px', borderRadius:'6px',
                        background:alpha(T.tx, 0.04), border:`1px solid ${T.bd}`,
                        color:T.mt, fontFamily:"'DM Mono',monospace" }}>{client.reg_number}</span>
                    )}
                    {client.vat_registered && client.vat_number && (
                      <span style={{ fontSize:'9px', padding:'2px 7px', borderRadius:'6px',
                        background:alpha(T.tx, 0.04), border:`1px solid ${T.bd}`, color:T.mt }}>
                        VAT {client.vat_number}
                      </span>
                    )}
                    {/* Show signing director badge */}
                    {signer && (
                      <span style={{ fontSize:'9px', padding:'2px 7px', borderRadius:'6px',
                        background:alpha(T.tx, 0.04), border:`1px solid ${T.bd}`, color:T.mt }}>
                        <Icon name="sign" /> {signer}
                        {directors.length > 1 && (
                          <span style={{ color:T.or, marginLeft:'4px' }}>
                            +{directors.length - 1} more
                          </span>
                        )}
                      </span>
                    )}
                  </div>

                  {/* Profile completeness */}
                  <div style={{ marginBottom:'14px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'9px', color:T.mt, marginBottom:'4px' }}>
                      <span>AFS profile</span>
                      <span style={{ color: completeness===100 ? T.gn : completeness>=60 ? T.yl : T.rd }}>
                        {completeness}% complete
                      </span>
                    </div>
                    <div style={{ height:'3px', borderRadius:'6px', background:T.bd, overflow:'hidden' }}>
                      <div style={{ height:'100%', borderRadius:'6px',
                        background: completeness===100 ? T.gn : T.or,
                        width:`${completeness}%`, transition:'width 0.4s' }}/>
                    </div>
                  </div>

                  {/* AFS History toggle */}
                  {!isArchived && (
                    <div style={{ marginBottom:'12px' }}>
                      <button
                        onClick={() => toggleHistory(client.id)}
                        style={{ width:'100%', padding:'8px 12px', borderRadius:'8px',
                          border:`1px solid ${histOpen ? alpha(T.or, 0.25) : T.bd}`,
                          background: histOpen ? T.ord : T.s2,
                          color: histOpen ? T.or : T.mt,
                          fontFamily:"'Outfit',sans-serif", fontSize:'12px', fontWeight:'600',
                          cursor:'pointer', display:'flex', justifyContent:'space-between',
                          alignItems:'center', transition:'all 0.2s' }}>
                        <span><Icon name="log" /> AFS History & Re-download</span>
                        <span style={{ fontSize:'10px', transition:'transform 0.2s',
                          transform: histOpen ? 'rotate(180deg)' : 'none' }}>▼</span>
                      </button>
                      {histOpen && (
                        <div style={{ marginTop:'8px', padding:'12px',
                          background:T.s2, borderRadius:'8px', border:`1px solid ${T.bd}` }}>
                          <ClientHistoryPanel client={client} allJobs={jobs} />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action buttons */}
                  {isArchived ? (
                    <button onClick={() => handleUnarchive(client)}
                      style={{ width:'100%', padding:'8px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                        background:'transparent', color:T.mt, fontFamily:"'Outfit',sans-serif",
                        fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
                      Restore Client
                    </button>
                  ) : (
                    <div style={{ display:'flex', gap:'4px' }}>
                      <button onClick={() => navigate('/trial-balance', { state:{client} })}
                        style={{ flex:1, padding:'8px', borderRadius:'6px', border:'none',
                          background:T.prg, color:T.onpri,
                          fontFamily:"'Outfit',sans-serif", fontSize:'12px', fontWeight:'700', cursor:'pointer' }}>
                        Generate AFS →
                      </button>
                      <button onClick={() => openEdit(client)}
                        style={{ padding:'8px 10px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                          background:'transparent', color:T.mt, fontFamily:"'Outfit',sans-serif",
                          fontSize:'12px', cursor:'pointer' }} title="Edit client"><Icon name="pencil" /></button>
                      <button onClick={() => navigate('/bank-statement', { state:{client} })}
                        style={{ padding:'8px 10px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                          background:'transparent', color:T.mt, fontFamily:"'Outfit',sans-serif",
                          fontSize:'12px', cursor:'pointer' }} title="Bank statement"><Icon name="bank" /></button>
                      <button onClick={() => setArchiveConfirm(client)}
                        style={{ padding:'8px 10px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                          background:'transparent', color:T.mt, fontFamily:"'Outfit',sans-serif",
                          fontSize:'12px', cursor:'pointer' }} title="Archive client"><Icon name="archive" /></button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes kailuxModalIn { from { opacity:0; transform:translateY(14px) scale(0.98); } to { opacity:1; transform:translateY(0) scale(1); } }`}</style>
    </div>
  )
}
